import { useChat } from '@ai-sdk/react';
import { Analytics } from '@cobliteam/analytics';
import useUser from '@cobliteam/hooks/src/useEntity/useUser';
import { useTranslation } from '@cobliteam/i18n';
import Alert from '@cobliteam/ui/components/Alert';
import { toAISdkV5Messages } from '@mastra/ai-sdk/ui';
import type { ChatStatus, UIMessage } from 'ai';
import { nanoid } from 'nanoid';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import AIAssistantHeader from '../../components/AIAssistantHeader';
import { stripAssistantPromptMetadata } from '../../components/AIEntityMentionsInput.utils';
import { rememberHiddenMessage } from '../../lib/hiddenPrompts';
import { chatTransport } from '../../lib/transporters';
import { useAIAssistant } from '../../store';
import type { AIAssistantPendingInput } from '../../store/useAIAssistant';
import { AGENTS, type ResourceId, type SendUserTextMessage } from '../../types';
import AIAssistantContent from './AIAssistantContent';

type SendMessage = SendUserTextMessage;

type AIAssistantChatProps = {
  showHeader?: boolean;
  handleGoBack?: () => void;
  isExpanded?: boolean;
  onExpand?: () => void;
  isSidepanelDrawer?: boolean;
  onToggleSidepanelLayout?: () => void;
  children: React.ReactNode | ((props: { sendMessage: SendMessage }) => React.ReactNode);
  placeholder?: string;
  resetChat?: boolean;
  headerActions?: React.ReactNode;
};

const AIAssistantChat = ({
  showHeader,
  handleGoBack,
  children,
  placeholder,
  isExpanded,
  onExpand,
  isSidepanelDrawer,
  onToggleSidepanelLayout,
  headerActions,
}: AIAssistantChatProps) => {
  const { threadId, thread, newThreadId } = useAIAssistant();
  const activeConversationId = threadId || newThreadId;

  const initialMessages = useMemo(() => {
    return thread?.messages ? toAISdkV5Messages(thread?.messages) : [];
  }, [thread?.messages]);

  return (
    <AIAssistantChatSession
      activeConversationId={activeConversationId}
      initialMessages={initialMessages}
      showHeader={showHeader}
      handleGoBack={handleGoBack}
      isExpanded={isExpanded}
      onExpand={onExpand}
      isSidepanelDrawer={isSidepanelDrawer}
      onToggleSidepanelLayout={onToggleSidepanelLayout}
      headerActions={headerActions}
      placeholder={placeholder}
    >
      {children}
    </AIAssistantChatSession>
  );
};

type AIAssistantChatSessionProps = AIAssistantChatProps & {
  activeConversationId: string;
  initialMessages: UIMessage[];
};

const PendingHiddenPromptSubmitter = ({
  activeConversationId,
  pendingInput,
  status,
  sendMessage,
}: {
  activeConversationId: string;
  pendingInput: AIAssistantPendingInput | null;
  status: ChatStatus;
  sendMessage: SendMessage;
}) => {
  const { clearPendingInput, createFakeThread, threadId } = useAIAssistant();
  const consumedPendingInputRef = useRef<string | null>(null);
  const submittingPendingInputRef = useRef<string | null>(null);
  const { t } = useTranslation('ai');

  useEffect(() => {
    if (!pendingInput?.hidden || status !== 'ready' || consumedPendingInputRef.current === pendingInput.id || submittingPendingInputRef.current === pendingInput.id) {
      return;
    }

    const prompt = pendingInput.input.trim();
    if (!prompt) {
      consumedPendingInputRef.current = pendingInput.id;
      clearPendingInput();
      return;
    }

    submittingPendingInputRef.current = pendingInput.id;

    const submitHiddenPrompt = async () => {
      try {
        if (!threadId) {
          await createFakeThread({ threadId: activeConversationId });
        }

        const messageId = nanoid();
        const trackedPrompt = stripAssistantPromptMetadata(prompt);
        Analytics.track('AI - Chat - Message Sent', {
          prompt: trackedPrompt,
          promptLength: trackedPrompt.length,
          hasMentions: false,
          source: 'sidepanel',
          messageId,
          hiddenPrompt: true,
        });
        rememberHiddenMessage({ id: messageId, text: prompt });
        await sendMessage({ role: 'user', id: messageId, metadata: { hidden: true }, parts: [{ type: 'text', text: prompt }] });
        consumedPendingInputRef.current = pendingInput.id;
        clearPendingInput();
      } catch (err) {
        consumedPendingInputRef.current = null;
        Alert.error(t('send_failed'));
        // eslint-disable-next-line no-console
        console.error('[AIAssistantChat] hidden prompt send failed:', err);
      } finally {
        submittingPendingInputRef.current = null;
      }
    };

    void submitHiddenPrompt();
  }, [activeConversationId, clearPendingInput, createFakeThread, pendingInput, sendMessage, status, t, threadId]);

  return null;
};

const AIAssistantChatSession = ({
  activeConversationId,
  initialMessages,
  showHeader,
  handleGoBack,
  children,
  placeholder,
  isExpanded,
  onExpand,
  isSidepanelDrawer,
  onToggleSidepanelLayout,
  headerActions,
}: AIAssistantChatSessionProps) => {
  const { threadId, refetchThread, refetchThreads, newThreadId, setThreadId, resetNewThreadId, pendingInput, sidepanelContext } = useAIAssistant();
  const { getUser, getSession } = useUser();
  const user = getUser();
  const session = getSession();
  const titleRefetchTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const previousConversationIdRef = useRef(activeConversationId);

  const transport = useMemo(() => {
    const userId = user?.id ?? 'unknownUser';
    const fleetId = session?.fleet?.id ?? 'unknownFleet';
    const resourceId: ResourceId = sidepanelContext?.resourceId ?? `assistant_${userId}_${fleetId}`;
    const url = window.location.pathname;

    return chatTransport({
      agentName: sidepanelContext?.agentName ?? AGENTS.agentFleetManager,
      source: 'web',
      component: sidepanelContext?.component ?? 'sidepanel',
      threadId: activeConversationId,
      resourceId,
      url,
      extraBody: sidepanelContext?.extraBody,
    });
  }, [activeConversationId, sidepanelContext, user?.id, session?.fleet?.id]);

  useEffect(() => {
    return () => clearTimeout(titleRefetchTimerRef.current);
  }, []);

  const { messages, error, status, sendMessage, regenerate, stop, setMessages } = useChat({
    id: activeConversationId,
    transport,
    messages: initialMessages,
    onFinish: async ({ message }) => {
      if (message.role !== 'assistant') return;
      if (!threadId) setThreadId(newThreadId);
      await refetchThread();
      // Delayed refetch to pick up the backend-generated thread title
      clearTimeout(titleRefetchTimerRef.current);
      titleRefetchTimerRef.current = setTimeout(() => refetchThreads(), 2000);
    },
  });

  // Seed useChat from server history WITHOUT remounting the session. Remounting on
  // `thread.messages.length` (the old `key` approach) caused a flick + re-scroll every time
  // a turn finished and `refetchThread` populated the thread. Mirrors AIChat.tsx: reseed only
  // when the conversation actually changes, or when history arrives for an empty chat.
  useEffect(() => {
    const hasConversationChanged = previousConversationIdRef.current !== activeConversationId;

    if (hasConversationChanged) {
      previousConversationIdRef.current = activeConversationId;
      setMessages(initialMessages);
      return;
    }

    if (threadId && initialMessages.length > 0 && messages.length === 0) {
      setMessages(initialMessages);
    }
  }, [activeConversationId, initialMessages, messages.length, setMessages, threadId]);

  const handleNewConversation = useCallback(() => {
    clearTimeout(titleRefetchTimerRef.current);
    stop();
    setMessages([]);
    setThreadId('');
    resetNewThreadId();
  }, [stop, setMessages, setThreadId, resetNewThreadId]);

  const resolvedMessages = messages.length > 0 ? messages : initialMessages;
  const hasMessages = resolvedMessages.length > 0;
  const emptyState = typeof children === 'function' ? children({ sendMessage }) : children;

  return (
    <section className='flex h-full flex-1 flex-col bg-ai-panel-gradient'>
      <AIAssistantHeader
        showHeader={showHeader}
        handleGoBack={handleGoBack}
        isExpanded={isExpanded}
        onExpand={onExpand}
        isSidepanelDrawer={isSidepanelDrawer}
        onToggleSidepanelLayout={onToggleSidepanelLayout}
        hasMessages={hasMessages}
        headerActions={headerActions}
        onNewConversation={handleNewConversation}
      />
      <AIAssistantContent
        emptyState={emptyState}
        placeholder={placeholder}
        messages={resolvedMessages}
        status={status}
        error={error}
        sendMessage={sendMessage}
        stop={stop}
        regenerate={regenerate}
        onRetry={handleNewConversation}
      />
      <PendingHiddenPromptSubmitter activeConversationId={activeConversationId} pendingInput={pendingInput} status={status} sendMessage={sendMessage} />
    </section>
  );
};

export default AIAssistantChat;
